import Vue from 'vue';
import { includes, filter, first } from 'lodash';
import { generate } from 'shortid';

const MAX_MESSAGE_COUNT = 5;
const MESSAGE_TYPES = ['success', 'info', 'warning', 'error'];
const DEFAULT_MESSAGE_TIMEOUT = 6000;
const POSITIONS = ['top-left', 'top-right', 'top-center', 'bottom-left', 'bottom-right', 'bottom-center'];
const state = Vue.observable({
  activeMessages: [],
  messagesQueue: [],
  position: 'top-center'
});
const methods = {
  addMessage: ({
    text,
    type,
    timeout,
    position,
    items
  }) => {
    if (!text) {
      return;
    }

    const message = {
      id: generate(),
      type: includes(MESSAGE_TYPES, type) ? type : 'info',
      text,
      items,
      timeout: timeout || DEFAULT_MESSAGE_TIMEOUT
    };

    if (state.activeMessages.length < MAX_MESSAGE_COUNT) {
      state.activeMessages.push(message);
      setTimeout(() => methods.removeMessage(message.id), message.timeout);
    } else {
      state.messagesQueue.push(message);
    }

    if (position) {
      state.position = includes(POSITIONS, position) ? position : 'top-center';
    }
  },
  removeMessage: id => {
    state.activeMessages = filter(state.activeMessages, message => message.id !== id);

    if (state.activeMessages.length < MAX_MESSAGE_COUNT && state.messagesQueue.length > 0) {
      const queuedMessage = first(state.messagesQueue);
      methods.addMessage(queuedMessage);
      state.messagesQueue = filter(state.messagesQueue, message => message.id !== queuedMessage.id);
    }
  }
};

//
var script = {
  data() {
    return {
      state
    };
  },

  computed: {
    activeMessages: () => state.activeMessages,
    position: () => state.position
  },
  methods: {
    getMessageIconName(type) {
      switch (type) {
        case 'success':
          return 'mdi-check-bold';

        case 'warning':
          return 'mdi-alert';

        case 'error':
          return 'mdi-close-circle';

        case 'info':
        default:
          return 'mdi-information';
      }
    },

    catchError(event) {
      console.error(event.error);
      methods.addMessage({
        text: event.message,
        type: 'error'
      });
      event.preventDefault();
      return true;
    },

    removeMessage(id) {
      methods.removeMessage(id);
    }

  },

  created() {
    window.addEventListener('error', this.catchError);
  },

  destroy() {
    window.removeEventListener('error', this.catchError);
  }

};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__ = script;
/* template */

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('ul', {
    class: [_vm.$style.messageList, _vm.$style[_vm.position]]
  }, _vm._l(_vm.activeMessages, function (message) {
    return _c('li', {
      key: message.id,
      class: [_vm.$style.messageListItem, _vm.$style[message.type], message.items && _vm.$style.withItems]
    }, [_c('div', {
      class: [_vm.$style.title, message.items && _vm.$style.withItems]
    }, [_c('v-icon', [_vm._v(_vm._s(_vm.getMessageIconName(message.type)))]), _vm._v("\n\t\t\t" + _vm._s(message.text) + "\n\t\t")], 1), _vm._v(" "), message.items ? _c('ul', _vm._l(message.items, function (item) {
      return _c('li', {
        key: item,
        class: _vm.$style.messageSubItem
      }, [_vm._v(_vm._s(item))]);
    }), 0) : _vm._e(), _vm._v(" "), _c('v-btn', {
      attrs: {
        "align": "right",
        "text": ""
      },
      on: {
        "click": function ($event) {
          return _vm.removeMessage(message.id);
        }
      }
    }, [_vm._v(_vm._s(_vm.$t('message.dismiss')))])], 1);
  }), 0);
};

var __vue_staticRenderFns__ = [];
/* style */

const __vue_inject_styles__ = function (inject) {
  if (!inject) return;
  inject("data-v-d2564a50_0", {
    source: ".src-Components-messageList-1NdL{position:absolute;display:flex;flex-direction:column;z-index:99999999;list-style:none;margin:0;padding:0}.src-Components-messageList-1NdL.src-Components-top-center-3RBX{top:8px;left:50%;transform:translateX(-50%)}.src-Components-messageList-1NdL.src-Components-top-left-18dJ{top:8px;left:8px;transform:translateX(0)}.src-Components-messageList-1NdL.src-Components-top-right-2rsw{top:8px;right:8px}.src-Components-messageList-1NdL.src-Components-bottom-center-qVNv{bottom:8px;left:50%;transform:translateX(-50%);flex-direction:column-reverse}.src-Components-messageList-1NdL.src-Components-bottom-left-lTQy{bottom:8px;left:8px;transform:translateX(0);flex-direction:column-reverse}.src-Components-messageList-1NdL.src-Components-bottom-right-eggS{bottom:8px;right:8px;transform:translateX(0);flex-direction:column-reverse}.src-Components-messageList-1NdL>.src-Components-messageListItem-3tOs{margin-bottom:8px}.src-Components-messageListItem-3tOs{display:flex;flex-direction:row;align-items:center;justify-content:space-between;font-size:.875rem;list-style:none;border-radius:4px;margin:0;padding:0;padding:8px 16px;box-shadow:0 3px 5px -1px rgba(0,0,0,.2),0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12);min-width:344px;max-width:672px}.src-Components-messageListItem-3tOs.src-Components-withItems-1cRq{flex-direction:column}.src-Components-messageListItem-3tOs.src-Components-success-3UYh{background-color:#4caf50}.src-Components-messageListItem-3tOs.src-Components-info-17gg{background-color:#2196f3}.src-Components-messageListItem-3tOs.src-Components-error-15Ag{background-color:#ff5252}.src-Components-messageListItem-3tOs.src-Components-warning-1q2b{background-color:#e1b157}.src-Components-messageListItem-3tOs>.src-Components-title-22yl{padding-right:10px}.src-Components-messageListItem-3tOs>.src-Components-title-22yl.src-Components-withItems-1cRq{align-self:start}.src-Components-messageListItem-3tOs>ul{display:block;align-self:start;margin:0 0 0 12px}.src-Components-messageListItem-3tOs>ul>.src-Components-messageSubItem-37cU{padding:0 10px}",
    map: undefined,
    media: undefined
  });
  Object.defineProperty(this, "$style", {
    value: {
      "messageList": "src-Components-messageList-1NdL",
      "top-center": "src-Components-top-center-3RBX",
      "top-left": "src-Components-top-left-18dJ",
      "top-right": "src-Components-top-right-2rsw",
      "bottom-center": "src-Components-bottom-center-qVNv",
      "bottom-left": "src-Components-bottom-left-lTQy",
      "bottom-right": "src-Components-bottom-right-eggS",
      "messageListItem": "src-Components-messageListItem-3tOs",
      "withItems": "src-Components-withItems-1cRq",
      "success": "src-Components-success-3UYh",
      "info": "src-Components-info-17gg",
      "error": "src-Components-error-15Ag",
      "warning": "src-Components-warning-1q2b",
      "title": "src-Components-title-22yl",
      "messageSubItem": "src-Components-messageSubItem-37cU"
    }
  });
};
/* scoped */


const __vue_scope_id__ = undefined;
/* module identifier */

const __vue_module_identifier__ = undefined;
/* functional template */

const __vue_is_functional_template__ = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__, __vue_module_identifier__, false, createInjector, undefined, undefined);

const messageSystemTranslations = {
  en: {
    message: {
      dismiss: 'Dismiss'
    }
  },
  de: {
    message: {
      dismiss: 'Entlassen'
    }
  }
};

const messageSystemApi = {
  addMessage: methods.addMessage
};

const install = Vue => {
  if (install.installed) return;
  install.installed = true;
  Object.defineProperty(Vue.prototype, 'messageSystem', {
    value: messageSystemApi
  });
  Vue.component('MessageContainer', __vue_component__);
};

const plugin = {
  install
};

export default plugin;
export { __vue_component__ as MessageContainer, messageSystemTranslations as MessageTranslations, messageSystemApi };
